import type { NavigationMenuItem } from '#ui/types';
import type { ComputedRef } from 'vue';

import { SellingMethod, AssetType } from '~/apiClient';
import { type PlatformSellingMethod } from '~/types/sale.type';

export type SlpNavigationItem = NavigationMenuItem & { sellingMethod: PlatformSellingMethod };

export interface UseSlpNavigationItemsReturn {
    navigationItems: ComputedRef<SlpNavigationItem[]>;
}

export const useSlpNavigationItems = (): UseSlpNavigationItemsReturn => {
    const { tt } = useTypedI18n();
    const route = useRoute();
    const localePath = useLocalePath();
    const { formatSlpRoute } = useSlpRouteUtils();
    const parsedRouteParams = useSlpParsedRouteParamsState(route);
    const sellingMethod = useSlpSellingMethod();
    const { trackBuyNowButton, trackAuctionsButton } = useTracking();
    const commonRouteParams = computed(() => ({
        ...parsedRouteParams.value,
        assetType: parsedRouteParams.value.assetType ?? AssetType.Bus,
    }));

    const alpPath = computed(() => {
        if (sellingMethod.value === SellingMethod.Auction) return route.fullPath;

        return localePath({
            ...formatSlpRoute({
                ...commonRouteParams.value,
                sellingMethod: SellingMethod.Auction,
            }),
            query: mapDslpToAlpFiltersQuery(route.query),
        });
    });

    const dslpPath = computed(() => {
        if (sellingMethod.value === SellingMethod.DirectSale) return route.fullPath;

        return localePath({
            ...formatSlpRoute({
                ...commonRouteParams.value,
                sellingMethod: SellingMethod.DirectSale,
            }),
            query: mapAlpToDslpFiltersQuery(route.query),
        });
    });

    const navigationItems = computed<SlpNavigationItem[]>(() => [
        {
            sellingMethod: SellingMethod.DirectSale,
            label: tt('slp.navigation.directSale'),
            icon: 'lucide:shopping-bag',
            to: dslpPath.value,
            onSelect: (): void => trackBuyNowButton(),
        },
        {
            sellingMethod: SellingMethod.Auction,
            label: tt('slp.navigation.auction'),
            icon: 'lucide:gavel',
            to: alpPath.value,
            onSelect: (): void => trackAuctionsButton(),
        },
    ]);

    return { navigationItems };
};
